/**
 * Brick Armory — <predictive-search> overlay (snippets/predictive-search.liquid).
 *
 * - Opens a modal <dialog> from any [data-search-open] trigger (header icon);
 *   without JS the trigger is a normal link to /search.
 * - Focus trap, Esc / backdrop / close button, focus returned to the trigger,
 *   soft slide-down (skipped with prefers-reduced-motion).
 * - Debounced /search/suggest.json (products, collections, pages, queries)
 *   rendered with DOM APIs only (no innerHTML from the response).
 * - Combobox + listbox: arrow keys move aria-activedescendant, Enter opens the
 *   highlighted result, Esc first clears the highlight, then closes.
 * - aria-live result count; recent searches kept in localStorage.
 */
(() => {
  if (customElements.get('predictive-search')) return;

  const STORAGE_KEY = 'brick:recent-searches';
  const MAX_RECENT = 5;
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Money (uses the shop's money format) ---------- */
  const formatMoney = (cents) => {
    const format = (window.BrickTheme && window.BrickTheme.moneyFormat) || '{{amount}}';
    const value = Number(cents) / 100;
    const group = (number, precision, thousands, decimal) => {
      const [whole, fraction] = number.toFixed(precision).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_space_separator':
          return group(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return group(value, 0, ' ', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        default:
          return group(value, 2, ',', '.');
      }
    });
  };

  const toCents = (value) => {
    if (typeof value === 'number') return value;
    const number = parseFloat(String(value || '0').replace(/[^\d.]/g, ''));
    return Math.round((Number.isNaN(number) ? 0 : number) * 100);
  };

  /* ---------- Tiny DOM helpers ---------- */
  const el = (tag, props = {}, children = []) => {
    const node = document.createElement(tag);
    Object.entries(props).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (key === 'className') node.className = value;
      else if (key === 'text') node.textContent = value;
      else node.setAttribute(key, value);
    });
    children.forEach((child) => child && node.append(child));
    return node;
  };

  /** Wraps the query match in <mark> using text nodes only. */
  const highlight = (text, terms) => {
    const fragment = document.createDocumentFragment();
    const index = terms ? text.toLowerCase().indexOf(terms.toLowerCase()) : -1;
    if (index < 0) {
      fragment.append(text);
      return fragment;
    }
    fragment.append(
      text.slice(0, index),
      el('mark', { text: text.slice(index, index + terms.length) }),
      text.slice(index + terms.length)
    );
    return fragment;
  };

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-search-dialog]');
      this.input = this.querySelector('[data-ps-input]');
      this.form = this.querySelector('form');
      this.results = this.querySelector('[data-ps-results]');
      this.idle = this.querySelector('[data-ps-idle]');
      this.status = this.querySelector('[data-ps-status]');
      this.recent = this.querySelector('[data-ps-recent]');
      this.recentList = this.querySelector('[data-ps-recent-list]');
      if (!this.dialog || !this.input || !this.form) return;

      this.cache = new Map();
      this.renderRecent();
      this.setupDialog();

      this.form.addEventListener('submit', (event) => {
        if (!this.input.value.trim()) {
          event.preventDefault();
          this.input.focus();
          return;
        }
        this.saveRecent(this.input.value);
      });

      const clear = this.querySelector('[data-ps-clear-recent]');
      if (clear) {
        clear.addEventListener('click', () => {
          try {
            localStorage.removeItem(STORAGE_KEY);
          } catch (error) {
            /* storage unavailable */
          }
          this.renderRecent();
          this.input.focus();
        });
      }

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.results.addEventListener('click', (event) => {
        if (event.target.closest('a')) this.saveRecent(this.input.value);
      });
      this.results.addEventListener('pointermove', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option && option !== this.selected) this.select(option, { scroll: false });
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTriggerClick);
    }

    /* ---------- Dialog ---------- */
    setupDialog() {
      this.onTriggerClick = (event) => {
        const trigger = event.target.closest('[data-search-open]');
        if (!trigger || event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
        event.preventDefault();
        this.open(trigger);
      };
      document.addEventListener('click', this.onTriggerClick);
      document.querySelectorAll('[data-search-open]').forEach((trigger) => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.dialog.id);
        trigger.setAttribute('aria-expanded', 'false');
      });

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.closest('[data-search-close]')) this.close();
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('keydown', (event) => this.trapFocus(event));
      this.dialog.addEventListener('close', () => {
        this.dialog.classList.remove('is-closing');
        this.select(null);
        if (this.trigger) {
          this.trigger.setAttribute('aria-expanded', 'false');
          this.trigger.focus({ preventScroll: true });
        }
      });
    }

    open(trigger) {
      if (this.dialog.open) return;
      this.trigger = trigger;
      document.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());
      this.dialog.showModal();
      trigger.setAttribute('aria-expanded', 'true');
      this.input.focus({ preventScroll: true });
      this.input.select();
    }

    close() {
      const dialog = this.dialog;
      if (!dialog.open || dialog.classList.contains('is-closing')) return;
      if (reducedMotion.matches) {
        dialog.close();
        return;
      }
      dialog.classList.add('is-closing');
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        dialog.close();
      };
      dialog.querySelector('.search-overlay__panel').addEventListener('animationend', finish, { once: true });
      window.setTimeout(finish, 350);
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const items = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter(
        (node) => node.getClientRects().length > 0 && node.getAttribute('tabindex') !== '-1'
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------- Combobox ---------- */
    onInput() {
      clearTimeout(this.timer);
      const terms = this.input.value.trim();
      if (!terms) {
        if (this.controller) this.controller.abort();
        this.removeAttribute('data-loading');
        this.clearResults();
        return;
      }
      this.timer = setTimeout(() => this.search(terms), 240);
    }

    onKeydown(event) {
      const options = this.options();
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!options.length) return;
        event.preventDefault();
        const index = options.indexOf(this.selected);
        const next =
          event.key === 'ArrowDown'
            ? options[(index + 1) % options.length]
            : options[(index - 1 + options.length) % options.length];
        this.select(next);
      } else if (event.key === 'Enter' && this.selected) {
        const link = this.selected.querySelector('a');
        if (link) {
          event.preventDefault();
          this.saveRecent(this.input.value);
          link.click();
        }
      } else if (event.key === 'Escape' && this.selected) {
        event.preventDefault();
        event.stopPropagation();
        this.select(null);
      } else if (event.key === 'Escape') {
        // A type=search input swallows the first Escape to clear itself; close the overlay instead
        event.preventDefault();
        this.close();
      }
    }

    options() {
      return this.results.hidden ? [] : Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    select(option, { scroll = true } = {}) {
      if (this.selected) this.selected.setAttribute('aria-selected', 'false');
      this.selected = option || null;
      if (option) {
        option.setAttribute('aria-selected', 'true');
        this.input.setAttribute('aria-activedescendant', option.id);
        if (scroll) option.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    async search(terms) {
      if (this.cache.has(terms)) {
        this.render(terms, this.cache.get(terms));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setAttribute('data-loading', '');
      this.announce(this.dataset.textLoading);

      const params = new URLSearchParams({
        q: terms,
        'resources[type]': 'product,collection,page,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
      });

      try {
        const response = await fetch(`${this.dataset.url}.json?${params}`, {
          signal: this.controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(String(response.status));
        const data = await response.json();
        const resources = (data.resources && data.resources.results) || {};
        this.cache.set(terms, resources);
        if (this.input.value.trim() === terms) this.render(terms, resources);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.clearResults();
      } finally {
        if (this.input.value.trim() === terms) this.removeAttribute('data-loading');
      }
    }

    icon(name) {
      const template = this.querySelector(`template[data-ps-icon="${name}"]`);
      return template ? template.content.firstElementChild.cloneNode(true) : null;
    }

    render(terms, { products = [], collections = [], pages = [], queries = [] }) {
      this.removeAttribute('data-loading');
      this.select(null);
      let uid = 0;
      const id = () => `${this.results.id}-option-${(uid += 1)}`;
      const option = (child, className = '') =>
        el('li', { role: 'option', id: id(), 'aria-selected': 'false', className: `ps-option ${className}`.trim() }, [
          child,
        ]);
      const group = (label, list, className = 'ps-list') => {
        const labelId = id();
        return el('div', { role: 'group', 'aria-labelledby': labelId, className: 'predictive-search__group' }, [
          el('p', { id: labelId, className: 'predictive-search__label', text: label }),
          el('ul', { role: 'presentation', className }, list),
        ]);
      };

      const side = [];
      const suggestionList = queries.slice(0, 4).map((query) => {
        const link = el('a', { className: 'ps-link', href: query.url, tabindex: '-1' }, [this.icon('search')]);
        link.append(highlight(query.text, terms));
        return option(link);
      });
      if (suggestionList.length) side.push(group(this.dataset.textSuggestions, suggestionList));

      const collectionList = collections.slice(0, 4).map((collection) =>
        option(el('a', { className: 'ps-link', href: collection.url, tabindex: '-1' }, [highlight(collection.title, terms)]))
      );
      if (collectionList.length) side.push(group(this.dataset.textCollections, collectionList));

      const pageList = pages.slice(0, 3).map((page) =>
        option(el('a', { className: 'ps-link', href: page.url, tabindex: '-1' }, [highlight(page.title, terms)]))
      );
      if (pageList.length) side.push(group(this.dataset.textPages, pageList));

      const productList = products.slice(0, 6).map((product) => {
        const src = (product.featured_image && product.featured_image.url) || product.image;
        const media = el('span', { className: 'ps-product__media stud-grid' });
        if (src) {
          const url = new URL(src, window.location.href);
          url.searchParams.set('width', '240');
          media.append(el('img', { src: url.toString(), alt: '', width: '240', height: '240', loading: 'lazy' }));
        }
        const price = toCents(product.price);
        const compare = toCents(product.compare_at_price_max);
        const priceEl = el('span', { className: 'ps-product__price' });
        if (compare > price) {
          priceEl.append(el('span', { className: 'price price--sale', text: formatMoney(price) }));
          priceEl.append(el('s', { className: 'price price--compare', text: formatMoney(compare) }));
        } else {
          priceEl.append(el('span', { className: 'price', text: formatMoney(price) }));
        }
        const title = el('span', { className: 'ps-product__title' });
        title.append(highlight(product.title, terms));
        return option(
          el('a', { className: 'ps-product', href: product.url, tabindex: '-1' }, [
            media,
            el('span', { className: 'ps-product__info' }, [title, priceEl]),
          ])
        );
      });

      const total = suggestionList.length + collectionList.length + pageList.length + productList.length;
      const nodes = [];

      if (total === 0) {
        nodes.push(
          el('p', { className: 'ps-empty', role: 'presentation', text: this.dataset.textNone.replace('[terms]', terms) })
        );
      } else {
        const columns = el('div', {
          role: 'presentation',
          className: `ps-columns${side.length ? '' : ' ps-columns--products-only'}`,
        });
        if (productList.length) columns.append(group(this.dataset.textProducts, productList, 'ps-products'));
        if (side.length) columns.append(el('div', { role: 'presentation', className: 'ps-side' }, side));
        nodes.push(columns);
      }

      const searchUrl = `${this.dataset.searchUrl}?q=${encodeURIComponent(terms)}&options%5Bprefix%5D=last`;
      const seeAll = el('a', { className: 'ps-link ps-link--all', href: searchUrl, tabindex: '-1' }, [
        el('span', { text: this.dataset.textSeeAll.replace('[terms]', terms) }),
        this.icon('arrow'),
      ]);
      nodes.push(el('ul', { role: 'presentation', className: 'ps-list ps-see-all' }, [option(seeAll)]));

      this.results.replaceChildren(...nodes);
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      if (this.idle) this.idle.hidden = true;

      const template = total === 1 ? this.dataset.textCountOne : this.dataset.textCountOther;
      this.announce(total === 0 ? this.dataset.textNone.replace('[terms]', terms) : template.replace('[count]', total));
    }

    clearResults() {
      this.select(null);
      this.results.hidden = true;
      this.results.replaceChildren();
      this.input.setAttribute('aria-expanded', 'false');
      if (this.idle) this.idle.hidden = false;
      this.announce('');
    }

    announce(message) {
      if (!this.status) return;
      clearTimeout(this.statusTimer);
      this.statusTimer = setTimeout(() => {
        this.status.textContent = message || '';
      }, 400);
    }

    /* ---------- Recent searches ---------- */
    readRecent() {
      try {
        const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(list) ? list.filter((item) => typeof item === 'string') : [];
      } catch (error) {
        return [];
      }
    }

    saveRecent(value) {
      const terms = String(value || '').trim();
      if (!terms) return;
      const list = [terms, ...this.readRecent().filter((item) => item.toLowerCase() !== terms.toLowerCase())].slice(
        0,
        MAX_RECENT
      );
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch (error) {
        /* storage unavailable */
      }
      this.renderRecent();
    }

    renderRecent() {
      if (!this.recent || !this.recentList) return;
      const list = this.readRecent();
      this.recent.hidden = list.length === 0;
      this.recentList.replaceChildren(
        ...list.map((terms) =>
          el('li', {}, [
            el('a', {
              className: 'ps-chip',
              href: `${this.dataset.searchUrl}?q=${encodeURIComponent(terms)}&options%5Bprefix%5D=last`,
              text: terms,
            }),
          ])
        )
      );
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
